import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ChatService, IChat, TextChatMessage, files } from '@bcl/chat';
import { ComponentChanges } from '@bcl/design-system';
import {
  debounceTime,
  filter,
  map,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import { UserChatMessage } from './chat.model';
import { AttachmentsService } from 'src/app/services/attachments.service';
import { HttpEventType } from '@angular/common/http';
import { CurrentUserService } from '@ews/users-management';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'pc-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnChanges, OnInit, OnDestroy {
  @Input()
  public chatId: string | undefined;

  public currentUserId: string | undefined;
  public messages: UserChatMessage[] = [];
  public isSendingFileMessage = false;

  private chatValue: IChat | undefined;

  private lastMessageReadTimestamp: Date | undefined;
  private messagesSubscription: Subscription | undefined;
  private markAsReadSubject$ = new Subject<UserChatMessage>();

  private readonly destroyed$ = new ReplaySubject<void>(1);

  constructor(
    private chatService: ChatService,
    private attachmentsService: AttachmentsService,
    private currentUserService: CurrentUserService,
    private element: ElementRef
  ) {}

  public get chat(): IChat | undefined {
    return this.chatValue;
  }
  public set chat(value: IChat | undefined) {
    this.messages = [];
    if (this.messagesSubscription) {
      this.messagesSubscription.unsubscribe();
      this.messagesSubscription = undefined;
    }

    if (value) {
      this.messagesSubscription = value.messages
        .pipe(takeUntil(this.destroyed$))
        .subscribe((x) => {
          const message = x as UserChatMessage;
          this.messages.push(message);

          if (
            this.currentUserId &&
            message.content.userId !== this.currentUserId
          ) {
            this.markAsReadSubject$.next(message);
          }
        });
    }

    this.chatValue = value;
  }

  public ngOnChanges(changes: ComponentChanges<ChatComponent>): void {
    if (changes.chatId) {
      if (changes.chatId.currentValue) {
        this.chatService
          .load(changes.chatId.currentValue)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((chat) => setTimeout(() => (this.chat = chat)));
      } else {
        this.chat = undefined;
      }
    }
  }

  public ngOnInit(): void {
    this.currentUserService.user
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.currentUserId = user.id;
      });

    this.markAsReadSubject$
      .pipe(debounceTime(500), takeUntil(this.destroyed$))
      .subscribe((message) => {
        this.markMessageAsReadIfNewer(message);
      });
  }

  public scrollToEnd(): void {
    const chatPreview = document.querySelector('pc-chat-preview');
    if (chatPreview) {
      chatPreview.scrollTop = chatPreview.scrollHeight;
    }

    this.element.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest',
    });
  }

  public sendTextMessage(text: string): void {
    if (!this.chat) {
      throw new Error('Chat is not loaded.');
    }
    if (!this.currentUserId) {
      throw new Error('Current user ID is undefined.');
    }

    this.chat
      .sendMessage(new TextChatMessage(text, this.currentUserId))
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
  }

  public sendFileMessage(file: File): void {
    if (!this.chat) {
      throw new Error('Chat is not loaded.');
    }
    if (!this.currentUserId) {
      throw new Error('Current user ID is undefined.');
    }

    this.isSendingFileMessage = true;
    this.saveFile(file)
      .pipe(
        switchMap((fileChatMessage) => this.chat!.sendMessage(fileChatMessage)),
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: () => (this.isSendingFileMessage = false),
        error: (error) => {
          this.isSendingFileMessage = false;
        },
      });
  }

  public ngOnDestroy(): void {
    this.messagesSubscription?.unsubscribe();
    this.markAsReadSubject$.complete();

    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onLastMessageRead(message: UserChatMessage): void {
    this.markMessageAsReadIfNewer(message);
  }

  private markMessageAsReadIfNewer(message: UserChatMessage): void {
    if (
      !this.lastMessageReadTimestamp ||
      message.timestamp > this.lastMessageReadTimestamp
    ) {
      this.chat!.markAsRead(message.timestamp)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => (this.lastMessageReadTimestamp = message.timestamp));
    }
  }

  private saveFile(file: File): Observable<files.FileChatMessage> {
    return this.attachmentsService.saveFile('Chat', this.chat!.id, file).pipe(
      filter((x) => x.type === HttpEventType.Response),
      map(
        (x) =>
          new files.FileChatMessage(
            x.body.referenceId,
            x.body.fileName,
            x.body.mimeType,
            x.body.size,
            this.currentUserId!
          )
      )
    );
  }
}
